import { Link } from 'react-router-dom';
import { DollarSign, Shield, Users, FileText, CheckCircle, ArrowRight, Clock, Award, Zap, Newspaper, Calendar } from 'lucide-react';

export const Grants = () => {
  return (
    <div className="pt-20" data-testid="grants-page">
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-[#5B4FF9]" data-testid="grants-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl">
            <p className="text-sm uppercase tracking-[0.2em] font-bold text-white/80 mb-4">
              Players Health Foundation · Grants Program
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-extrabold text-white leading-tight mb-6">
              Safer sports.<br />More kids playing.
            </h1>
            <p className="text-xl leading-relaxed text-white/90 mb-8">
              The PHF Grant Program provides funding and safety resources to youth sports organizations committed to removing financial barriers to participation — and protecting every athlete in their care.
            </p>
            <a
              href="https://playershealthfoundation.submittable.com/submit"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-[#5B4FF9] rounded-full px-8 py-3 font-semibold transition-all transform hover:-translate-y-1 shadow-sm hover:shadow-lg inline-flex items-center justify-center"
              data-testid="grants-hero-apply-btn"
            >
              Start Application
              <ArrowRight className="ml-2 h-5 w-5" />
            </a>
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="py-20 md:py-32" data-testid="whats-included">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#5B4FF9] mb-4">
              What's Included
            </p>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-[#272926] leading-snug">
              Funding + Safety Resources
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Accessibility Funding */}
            <div className="bg-white border border-zinc-200 rounded-3xl p-8 shadow-sm" data-testid="funding-card">
              <div className="flex items-center mb-6">
                <div className="w-14 h-14 bg-[#F5F4FF] rounded-2xl flex items-center justify-center mr-4">
                  <DollarSign className="h-7 w-7 text-[#5B4FF9]" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-[#272926]">Accessibility Funding</h3>
                </div>
              </div>
              <p className="text-zinc-600 leading-relaxed mb-6">
                Direct grant funding your organization can put to work immediately — covering the operational costs that make youth participation possible.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {['Registration & enrollment', 'Uniforms & equipment', 'Travel & tournaments', 'Coaching staff', 'Background checks', 'General operations'].map((item, index) => (
                  <div key={index} className="flex items-center text-sm text-zinc-700">
                    <CheckCircle className="h-4 w-4 text-[#5B4FF9] mr-2 flex-shrink-0" />
                    {item}
                  </div>
                ))}
              </div>
            </div>

            {/* Safety Resources */}
            <div className="bg-[#272926] rounded-3xl p-8 text-white" data-testid="safety-card">
              <div className="flex items-center mb-6">
                <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mr-4">
                  <Shield className="h-7 w-7 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">Safety Resources</h3>
                  <span className="text-sm text-green-400 font-semibold">Free</span>
                </div>
              </div>
              <p className="text-zinc-300 leading-relaxed mb-6">
                All grant recipients get access to PHF's Safety Hub — including abuse prevention training for every adult working with minor athletes.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {['Abuse prevention training', 'Policy templates', 'Safety Hub access', 'Compliance resources'].map((item, index) => (
                  <div key={index} className="flex items-center text-sm text-zinc-300">
                    <CheckCircle className="h-4 w-4 text-green-400 mr-2 flex-shrink-0" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who Should Apply Section */}
      <section className="py-20 md:py-32 bg-zinc-50" data-testid="who-should-apply">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#5B4FF9] mb-4">
              Who Should Apply
            </p>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-[#272926] leading-snug">
              Is your organization a good fit?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl p-6 border border-zinc-200">
              <div className="w-12 h-12 bg-[#F5F4FF] rounded-xl flex items-center justify-center mb-4">
                <Users className="h-6 w-6 text-[#5B4FF9]" />
              </div>
              <h3 className="text-lg font-bold text-[#272926] mb-2">Serves underserved youth</h3>
              <p className="text-zinc-600 text-sm leading-relaxed">
                Programs where cost is a meaningful barrier to participation for families
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-zinc-200">
              <div className="w-12 h-12 bg-[#F5F4FF] rounded-xl flex items-center justify-center mb-4">
                <Zap className="h-6 w-6 text-[#5B4FF9]" />
              </div>
              <h3 className="text-lg font-bold text-[#272926] mb-2">Youth-focused organization</h3>
              <p className="text-zinc-600 text-sm leading-relaxed">
                Programs that work directly with minor athletes across any sport
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-zinc-200">
              <div className="w-12 h-12 bg-[#F5F4FF] rounded-xl flex items-center justify-center mb-4">
                <Shield className="h-6 w-6 text-[#5B4FF9]" />
              </div>
              <h3 className="text-lg font-bold text-[#272926] mb-2">Committed to athlete safety</h3>
              <p className="text-zinc-600 text-sm leading-relaxed">
                Willing to implement safety policies and complete training for all adult staff
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-zinc-200">
              <div className="w-12 h-12 bg-[#F5F4FF] rounded-xl flex items-center justify-center mb-4">
                <FileText className="h-6 w-6 text-[#5B4FF9]" />
              </div>
              <h3 className="text-lg font-bold text-[#272926] mb-2">Ready to put funds to work</h3>
              <p className="text-zinc-600 text-sm leading-relaxed">
                A clear sense of how grant dollars will reduce barriers for the kids you serve
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 md:py-32" data-testid="how-it-works">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#5B4FF9] mb-4">
              How It Works
            </p>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-[#272926] leading-snug">
              Your path to funding
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Step 1 */}
            <div className="relative">
              <div className="w-12 h-12 bg-[#5B4FF9] rounded-full flex items-center justify-center text-white font-bold text-xl mb-6">
                1
              </div>
              <h3 className="text-lg font-bold text-[#272926] mb-3">Complete the application</h3>
              <p className="text-zinc-600 text-sm leading-relaxed">
                Tell us about your organization, the community you serve, and how grant funds will be used to increase access for youth in your program.
              </p>
            </div>

            {/* Step 2 */}
            <div className="relative">
              <div className="w-12 h-12 bg-[#5B4FF9] rounded-full flex items-center justify-center text-white font-bold text-xl mb-6">
                2
              </div>
              <h3 className="text-lg font-bold text-[#272926] mb-3">Review & award</h3>
              <p className="text-zinc-600 text-sm leading-relaxed mb-4">
                PHF reviews submissions and selects organizations that demonstrate genuine community need and a clear plan to put funds toward participation.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                <p className="text-xs text-amber-800">
                  <Clock className="h-3 w-3 inline mr-1" />
                  Applications are reviewed on a rolling cycle — you'll be notified of your award cycle at the time of selection.
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="relative">
              <div className="w-12 h-12 bg-[#5B4FF9] rounded-full flex items-center justify-center text-white font-bold text-xl mb-6">
                3
              </div>
              <h3 className="text-lg font-bold text-[#272926] mb-3">Receive funds & Safety Hub access</h3>
              <p className="text-zinc-600 text-sm leading-relaxed">
                Grant funds are disbursed directly to your organization. Safety Hub access and abuse prevention training begin right away for your team.
              </p>
            </div>

            {/* Step 4 */}
            <div className="relative">
              <div className="w-12 h-12 bg-[#5B4FF9] rounded-full flex items-center justify-center text-white font-bold text-xl mb-6">
                4
              </div>
              <h3 className="text-lg font-bold text-[#272926] mb-3">Get kids playing safely</h3>
              <p className="text-zinc-600 text-sm leading-relaxed">
                Put funds toward the costs that matter most for your program, while building a safer environment for every athlete you serve.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* News & Resources Section */}
      <section className="py-20 md:py-32 bg-zinc-50" data-testid="grants-resources">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-12">
            <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#5B4FF9] mb-4">
              News & Resources
            </p>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-[#272926] leading-snug">
              Latest Updates
            </h2>
          </div>

          <div className="max-w-2xl mx-auto">
            <Link
              to="/grants/press-release"
              className="block bg-white border border-zinc-200 rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all hover:-translate-y-1 group"
              data-testid="press-release-link"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#F5F4FF] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-[#5B4FF9] transition-colors">
                  <Newspaper className="h-6 w-6 text-[#5B4FF9] group-hover:text-white transition-colors" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-semibold text-[#5B4FF9] bg-[#F5F4FF] px-2 py-1 rounded-full">Press Release</span>
                    <span className="text-xs text-zinc-500 flex items-center">
                      <Calendar className="h-3 w-3 mr-1" />
                      May 21, 2026
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-[#272926] mb-2 group-hover:text-[#5B4FF9] transition-colors">
                    Players Health Foundation Launches $100,000 Grant Program to Protect Young Athletes
                  </h3>
                  <p className="text-zinc-600 text-sm leading-relaxed">
                    Twenty grants of $5,000 each will fund youth sports organizations committed to athlete safety, training support, and community impact.
                  </p>
                  <span className="inline-flex items-center text-[#5B4FF9] font-semibold text-sm mt-4">
                    Read more
                    <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-[#5B4FF9]" data-testid="grants-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-2xl mx-auto text-center">
            <Award className="h-16 w-16 text-white/80 mx-auto mb-6" />
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-white leading-snug mb-4">
              Ready to apply?
            </h2>
            <p className="text-lg text-white/90 mb-4">
              Applications take about 20–30 minutes. Have your organization details and a sense of the community need you're addressing ready to go.
            </p>
            <p className="text-sm text-white/70 mb-8">
              Organizations serving communities where the majority of families face financial barriers to sports participation are especially encouraged to apply.
            </p>
            <a
              href="https://playershealthfoundation.submittable.com/submit"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-[#5B4FF9] rounded-full px-8 py-4 font-semibold transition-all transform hover:-translate-y-1 shadow-sm hover:shadow-lg inline-flex items-center justify-center text-lg"
              data-testid="start-application-btn"
            >
              Start application
              <ArrowRight className="ml-2 h-5 w-5" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
